import Delaunator from "delaunator";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function buildCrsOptions(app, selected) {
  const sel = String(selected || "").toUpperCase();
  return app.services.projections.listCRS()
    .map((crs) => `<option value="${esc(crs.code)}" ${crs.code.toUpperCase() === sel ? "selected" : ""}>${esc(crs.code)} - ${esc(crs.name)}</option>`)
    .join("");
}

function llKey(lat, lon) {
  return `${Number(lat).toFixed(7)}|${Number(lon).toFixed(7)}`;
}

function triArea(x1, y1, x2, y2, x3, y3) {
  return Math.abs(((x2 - x1) * (y3 - y1) - (x3 - x1) * (y2 - y1)) * 0.5);
}

export const volumeCalculationTool = {
  id: "volume-calculation",
  name: "Volume Calculation",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore?.(data); },
};

function createRuntime(app) {
  let container = null;
  let sourceCRS = "EPSG:2273";
  let primaryId = "";
  let secondaryId = "";
  let soundingCatalog = [];
  let offCatalog = null;
  let lastHistogram = null;
  let histMode = "dz";
  let lastDzSamples = [];
  let lastIhoRatioSamples = [];
  const el = {};

  function $id(id) { return container?.querySelector?.(`#${id}`) || null; }
  function setSummary(html) { if (el.summary) el.summary.innerHTML = html; }
  function setHistogramHtml(html) { if (el.histogram) el.histogram.innerHTML = html; }
  function getLayerById(id) { return soundingCatalog.find((l) => l.globalLayerId === id || l.layerId === id) || null; }
  function ready() { return !!getLayerById(primaryId) && !!getLayerById(secondaryId) && primaryId !== secondaryId; }

  function updateButtons() {
    const isReady = ready();
    if (el.computeBtn) el.computeBtn.disabled = !isReady;
    if (el.clearBtn) el.clearBtn.disabled = !isReady;
    if (!isReady) {
      setSummary("No calculation run yet. Select Surface A and Surface B sounding layers, then click <strong>Compute Volume</strong>.");
      setHistogramHtml('<div class="small">No histogram yet.</div>');
    }
  }

  function buildHistogram(values, { binCount = 60, symmetric = true, mode = "dz" } = {}) {
    if (!Array.isArray(values) || !values.length) return null;
    const nums = values.map((v) => Number(v)).filter((v) => Number.isFinite(v));
    if (!nums.length) return null;
    const q = 0.98; // clip tail outliers for a more representative distribution
    let min = 0;
    let max = 1;
    if (symmetric) {
      const absVals = nums.map((v) => Math.abs(v)).sort((a, b) => a - b);
      const idx = Math.min(absVals.length - 1, Math.max(0, Math.floor(q * (absVals.length - 1))));
      const pAbs = absVals[idx];
      const range = Math.max(1e-6, pAbs * 1.2);
      min = -range;
      max = range;
    } else {
      const sorted = nums.slice().sort((a, b) => a - b);
      const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(q * (sorted.length - 1))));
      const p = sorted[idx];
      min = 0;
      max = Math.max(1e-6, p * 1.2);
    }
    const span = max - min;
    const bins = Array.from({ length: binCount }, () => 0);
    let clipped = 0;
    nums.forEach((v) => {
      if (v < min || v > max) clipped += 1;
      let idx = Math.floor(((v - min) / span) * binCount);
      if (idx < 0) idx = 0;
      if (idx >= binCount) idx = binCount - 1;
      bins[idx] += 1;
    });
    const maxCount = Math.max(...bins, 1);
    const zeroIdx = Math.floor(((0 - min) / span) * binCount);
    const mean = nums.reduce((s, v) => s + v, 0) / nums.length;
    return { min, max, bins, maxCount, zeroIdx, mean, n: nums.length, clipped, quantile: q, mode };
  }

  function renderHistogram(hist) {
    if (!hist) {
      setHistogramHtml('<div class="small">No histogram yet.</div>');
      return;
    }
    const bars = hist.bins.map((count, i) => {
      const h = Math.max(2, Math.round((count / hist.maxCount) * 100));
      let color = "#60a5fa";
      if (hist.mode === "dz") {
        const isCut = i > hist.zeroIdx;
        const isFill = i < hist.zeroIdx;
        color = isCut ? "#f97316" : isFill ? "#3b82f6" : "#94a3b8";
      } else {
        const binCenter = hist.min + ((i + 0.5) / hist.bins.length) * (hist.max - hist.min);
        if (binCenter <= 1) color = "#22c55e";
        else if (binCenter <= 1.5) color = "#f59e0b";
        else color = "#ef4444";
      }
      return `<div title="bin ${i + 1}: ${count} pts" style="flex:1 1 0;height:${h}%;background:${color};border-radius:2px 2px 0 0;min-width:2px;"></div>`;
    }).join("");
    const minTxt = hist.min.toFixed(2);
    const maxTxt = hist.max.toFixed(2);
    const meanTxt = hist.mean.toFixed(2);
    const zeroIn = hist.zeroIdx >= 0 && hist.zeroIdx < hist.bins.length;
    const clippedTxt = Number(hist.clipped || 0) > 0
      ? ` | Clipped: <strong>${Number(hist.clipped).toLocaleString()}</strong>`
      : "";
    setHistogramHtml(`
      <div style="border:1px solid rgba(148,163,184,.25);border-radius:8px;padding:8px;background:rgba(2,6,23,.45);">
        <div style="height:96px;display:flex;align-items:flex-end;gap:1px;">
          ${bars}
        </div>
        <div class="row" style="justify-content:space-between;margin-top:6px;">
          <span class="small">${minTxt}${hist.mode === "dz" ? " ft" : ""}</span>
          <span class="small">${hist.mode === "dz" ? `0 ${zeroIn ? "inside range" : "outside range"}` : "IHO ratio"}</span>
          <span class="small">${maxTxt}${hist.mode === "dz" ? " ft" : ""}</span>
        </div>
        <div class="small" style="margin-top:4px;">Mean ${hist.mode === "dz" ? `dZ: <strong>${meanTxt} ft</strong>` : `ratio: <strong>${meanTxt}</strong>`} | Samples: <strong>${hist.n.toLocaleString()}</strong>${clippedTxt}</div>
        <div class="small" style="margin-top:2px;opacity:.85;">Range uses central ${(Number(hist.quantile || 0.98) * 100).toFixed(0)}%${hist.mode === "dz" ? " magnitude" : ""} with 20% headroom.</div>
      </div>
    `);
  }

  function rebuildHistogram() {
    if (histMode === "iho") {
      lastHistogram = buildHistogram(lastIhoRatioSamples, { binCount: 60, symmetric: false, mode: "iho" });
    } else {
      lastHistogram = buildHistogram(lastDzSamples, { binCount: 60, symmetric: true, mode: "dz" });
    }
    renderHistogram(lastHistogram);
  }

  function renderLayerOptions() {
    if (!container) return;
    const rows = soundingCatalog
      .map((l) => `<option value="${esc(l.globalLayerId || l.layerId)}">${esc(l.name)} (${Number(l.featureCount || 0).toLocaleString()} pts)</option>`)
      .join("");
    if (el.primarySel) {
      el.primarySel.innerHTML = `<option value="">Select primary...</option>${rows}`;
      if (primaryId) el.primarySel.value = primaryId;
    }
    if (el.secondarySel) {
      el.secondarySel.innerHTML = `<option value="">Select secondary...</option>${rows}`;
      if (secondaryId) el.secondarySel.value = secondaryId;
    }
    if (!getLayerById(primaryId)) primaryId = "";
    if (!getLayerById(secondaryId)) secondaryId = "";
    if (el.primarySel && primaryId) el.primarySel.value = primaryId;
    if (el.secondarySel && secondaryId) el.secondarySel.value = secondaryId;
    updateButtons();
  }

  function buildShared(primary, secondary) {
    const secondaryByLL = new Map();
    (secondary.records || []).forEach((r) => secondaryByLL.set(llKey(r.lat, r.lon), r));
    const shared = [];
    let unmatchedPrimary = 0;
    (primary.records || []).forEach((a) => {
      const b = secondaryByLL.get(llKey(a.lat, a.lon));
      if (!b) {
        unmatchedPrimary += 1;
        return;
      }
      let xy = null;
      try {
        xy = app.services.projections.transformCoord([Number(a.lon), Number(a.lat)], "EPSG:4326", sourceCRS);
      } catch {
        xy = null;
      }
      if (!xy || !Number.isFinite(Number(xy[0])) || !Number.isFinite(Number(xy[1]))) return;
      shared.push({
        x: Number(xy[0]),
        y: Number(xy[1]),
        zA: Number(a.z),
        zB: Number(b.z),
      });
    });
    return { shared, unmatchedPrimary };
  }

  async function compute() {
    const surfaceA = getLayerById(primaryId);
    const surfaceB = getLayerById(secondaryId);
    if (!surfaceA || !surfaceB || primaryId === secondaryId) return;

    setSummary(`Reading surfaces <strong>${esc(surfaceA.name)}</strong> and <strong>${esc(surfaceB.name)}</strong> and building TIN...`);
    if (el.computeBtn) el.computeBtn.disabled = true;

    try {
      const { shared, unmatchedPrimary } = buildShared(surfaceA, surfaceB);
      if (shared.length < 3) {
        setSummary("Not enough shared points between selected sounding layers to build a TIN.");
        updateButtons();
        return;
      }

      const coords = shared.map((p) => [p.x, p.y]);
      const delaunay = Delaunator.from(coords);
      const triangles = delaunay.triangles;
      if (!triangles || triangles.length < 3) {
        setSummary("Unable to build a TIN (not enough points or degenerate configuration).");
        updateButtons();
        return;
      }

      let totalArea = 0;
      let cutVolume = 0;
      let fillVolume = 0;
      const dzSamples = [];
      const ihoRatioSamples = [];

      for (let t = 0; t < triangles.length; t += 3) {
        const i0 = triangles[t];
        const i1 = triangles[t + 1];
        const i2 = triangles[t + 2];
        const p0 = shared[i0];
        const p1 = shared[i1];
        const p2 = shared[i2];

        const area = triArea(p0.x, p0.y, p1.x, p1.y, p2.x, p2.y);
        if (!Number.isFinite(area) || area <= 0) continue;
        totalArea += area;

        const zAmean = (p0.zA + p1.zA + p2.zA) / 3;
        const zBmean = (p0.zB + p1.zB + p2.zB) / 3;
        const dz = zAmean - zBmean;
        dzSamples.push(dz);
        const depthFt = Math.abs((zAmean + zBmean) / 2);
        const depthM = depthFt * 0.3048;
        const tvuSingleM = Math.sqrt((0.25 * 0.25) + ((0.0075 * depthM) * (0.0075 * depthM)));
        const tvuCombinedFt = (Math.sqrt(2) * tvuSingleM) / 0.3048;
        if (Number.isFinite(tvuCombinedFt) && tvuCombinedFt > 0) ihoRatioSamples.push(Math.abs(dz) / tvuCombinedFt);
        if (dz > 0) cutVolume += dz * area;
        else if (dz < 0) fillVolume += (-dz) * area;
      }

      const netVolume = cutVolume - fillVolume;
      let html = "";
      html += `Shared points used: <strong>${shared.length.toLocaleString()}</strong>.<br/>`;
      html += `Unmatched primary points: <strong>${unmatchedPrimary.toLocaleString()}</strong>.<br/>`;
      html += `Number of triangles: <strong>${(triangles.length / 3).toLocaleString()}</strong>.<br/>`;
      html += `Planar area (approx.): <strong>${Math.round(totalArea).toLocaleString()} ft²</strong>.<br/><br/>`;
      html += "<strong>Volumes (A - B)</strong><br/>";
      html += `Cut (A above B): <strong>${Math.round(cutVolume).toLocaleString()} ft³</strong><br/>`;
      html += `Fill (B above A): <strong>${Math.round(fillVolume).toLocaleString()} ft³</strong><br/>`;
      html += `Net (Cut - Fill): <strong>${Math.round(netVolume).toLocaleString()} ft³</strong>`;
      if (ihoRatioSamples.length) {
        const n = ihoRatioSamples.length;
        const le1 = ihoRatioSamples.filter((v) => v <= 1).length;
        const gt1 = n - le1;
        const gt15 = ihoRatioSamples.filter((v) => v > 1.5).length;
        const gt2 = ihoRatioSamples.filter((v) => v > 2).length;
        html += "<br/><br/><strong>IHO Special Order Screening (|dZ| / TVUcombined)</strong><br/>";
        html += `Within envelope (<= 1.0): <strong>${((100 * le1) / n).toFixed(1)}%</strong><br/>`;
        html += `Outside envelope (> 1.0): <strong>${((100 * gt1) / n).toFixed(1)}%</strong> | >1.5: <strong>${((100 * gt15) / n).toFixed(1)}%</strong> | >2.0: <strong>${((100 * gt2) / n).toFixed(1)}%</strong>`;
      }
      setSummary(html);
      lastDzSamples = dzSamples;
      lastIhoRatioSamples = ihoRatioSamples;
      rebuildHistogram();
      updateButtons();
    } catch (err) {
      console.error("[VolumeCalculation] compute failed", err);
      setSummary(`Error while computing volume: ${esc(err?.message || String(err))}`);
      lastDzSamples = [];
      lastIhoRatioSamples = [];
      lastHistogram = null;
      renderHistogram(lastHistogram);
      updateButtons();
    }
  }

  function clear() {
    primaryId = "";
    secondaryId = "";
    lastDzSamples = [];
    lastIhoRatioSamples = [];
    lastHistogram = null;
    if (el.primarySel) el.primarySel.value = "";
    if (el.secondarySel) el.secondarySel.value = "";
    renderHistogram(lastHistogram);
    updateButtons();
  }

  function onClick(e) {
    const id = e.target?.id;
    if (!id) return;
    if (id === "vol-compute") {
      compute();
      return;
    }
    if (id === "vol-clear") clear();
  }

  function onChange(e) {
    const id = e.target?.id;
    if (!id) return;
    if (id === "vol-source-crs") {
      sourceCRS = String(el.sourceCRS?.value || "EPSG:2273");
      return;
    }
    if (id === "vol-hist-mode") {
      histMode = String(e.target.value || "dz");
      rebuildHistogram();
      return;
    }
    if (id === "vol-primary-layer") primaryId = e.target.value || "";
    if (id === "vol-secondary-layer") secondaryId = e.target.value || "";
    if (id === "vol-primary-layer" || id === "vol-secondary-layer") {
      if (ready()) {
        const a = getLayerById(primaryId);
        const b = getLayerById(secondaryId);
        setSummary(`Surface A: <strong>${esc(a?.name || "")}</strong>; Surface B: <strong>${esc(b?.name || "")}</strong>. Click <strong>Compute Volume</strong>.`);
      }
      updateButtons();
    }
  }

  function render() {
    if (!container) return;
    const crsOptions = buildCrsOptions(app, sourceCRS);
    container.innerHTML = `
      <div class="card">
        <div class="small" style="margin-bottom:6px;">
          Compute cut / fill volumes between two loaded sounding surfaces over their shared area.
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap;align-items:flex-end;">
          <div style="flex:1 1 180px;">
            <label class="muted">Surface A (Existing)</label><br/>
            <select id="vol-primary-layer" style="width:100%;"></select>
          </div>
          <div style="flex:1 1 180px;">
            <label class="muted">Surface B (Design / Template)</label><br/>
            <select id="vol-secondary-layer" style="width:100%;"></select>
          </div>
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap;align-items:flex-end;margin-top:6px;">
          <div>
            <label class="muted">Planar CRS</label><br/>
            <select id="vol-source-crs" style="max-width:240px;">${crsOptions}</select>
          </div>
          <div class="muted" style="font-size:11px;">
            Volumes are computed as A - B (positive = cut).
          </div>
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px;">
          <button id="vol-compute" class="btn primary" disabled>Compute Volume</button>
          <button id="vol-clear" class="btn" disabled>Clear</button>
          <label class="small" style="margin-left:auto;">Histogram mode
            <select id="vol-hist-mode" style="margin-left:6px;">
              <option value="dz" ${histMode === "dz" ? "selected" : ""}>dZ (A-B)</option>
              <option value="iho" ${histMode === "iho" ? "selected" : ""}>IHO normalized</option>
            </select>
          </label>
        </div>
        <div class="field" style="margin-top:8px;">
          <label>Volume Summary</label>
          <div id="vol-summary" class="muted cd-output">
            No calculation run yet. Select Surface A and Surface B sounding layers, then click <strong>Compute Volume</strong>.
          </div>
        </div>
        <div class="field" style="margin-top:8px;">
          <label>dZ Histogram (A - B)</label>
          <div id="vol-histogram"></div>
        </div>
      </div>
    `;
    el.primarySel = $id("vol-primary-layer");
    el.secondarySel = $id("vol-secondary-layer");
    el.sourceCRS = $id("vol-source-crs");
    el.computeBtn = $id("vol-compute");
    el.clearBtn = $id("vol-clear");
    el.summary = $id("vol-summary");
    el.histogram = $id("vol-histogram");
    renderHistogram(lastHistogram);
    renderLayerOptions();
  }

  function mount(next) {
    if (container === next) return;
    container = next;
    render();
    container.addEventListener("click", onClick);
    container.addEventListener("change", onChange);
    offCatalog = app.events.on("soundings:catalog:changed", ({ layers } = {}) => {
      soundingCatalog = Array.isArray(layers) ? layers : [];
      renderLayerOptions();
    });
    app.events.emit("soundings:catalog:request");
  }

  function snapshot() {
    return {
      sourceCRS,
      primaryId,
      secondaryId,
      histMode,
      summaryHtml: el.summary?.innerHTML || "",
      histogram: lastHistogram,
    };
  }

  function restore(data) {
    if (!data || typeof data !== "object") return;
    sourceCRS = String(data.sourceCRS || sourceCRS);
    primaryId = String(data.primaryId || primaryId);
    secondaryId = String(data.secondaryId || secondaryId);
    histMode = data.histMode === "iho" ? "iho" : "dz";
    lastHistogram = data.histogram && typeof data.histogram === "object" ? data.histogram : null;
    if (el.sourceCRS) el.sourceCRS.value = sourceCRS;
    if (typeof data.summaryHtml === "string" && data.summaryHtml.trim() && el.summary) el.summary.innerHTML = data.summaryHtml;
    renderHistogram(lastHistogram);
    renderLayerOptions();
  }

  function destroy() {
    if (container) {
      container.removeEventListener("click", onClick);
      container.removeEventListener("change", onChange);
    }
    try { offCatalog?.(); } catch {}
    offCatalog = null;
    container = null;
  }

  return { mount, snapshot, restore, destroy };
}
